import React,{useState}from'react';
import*as FiIcons from'react-icons/fi';
import SafeIcon from'../common/SafeIcon';
import{questionTypes}from'../data';
import QuestionOptionEditor from'./QuestionOptionEditor';
import RepeatingFieldsEditor from'./RepeatingFieldsEditor';
import'./questionnaire-templates.css';

const{
  FiChevronDown,
  FiChevronUp,
  FiGripVertical,
  FiPlus,
  FiSave,
  FiTrash2,
  FiX
}=FiIcons;

function cloneSections(sections){
  return JSON.parse(JSON.stringify(sections));
}

function QuestionnaireTemplateEditor({
  template,
  onClose,
  onSave
}){
  const[sections,setSections]=useState(
    ()=>cloneSections(template.sections)
  );
  const[draggedQuestion,setDraggedQuestion]=useState(null);

  const updateSection=(sectionId,changes)=>{
    setSections((current)=>current.map((section)=>(
      section.id===sectionId
        ?{...section,...changes}
        :section
    )));
  };

  const updateQuestion=(sectionId,questionId,changes)=>{
    setSections((current)=>current.map((section)=>{
      if(section.id!==sectionId)return section;

      return{
        ...section,
        questions:section.questions.map((question)=>(
          question.id===questionId
            ?{...question,...changes}
            :question
        ))
      };
    }));
  };

  const addQuestion=(sectionId)=>{
    const section=sections.find((item)=>item.id===sectionId);
    if(!section)return;

    updateSection(sectionId,{
      questions:[
        ...section.questions,
        {
          id:`question-${Date.now()}`,
          title:'New question',
          type:'Short text',
          required:false,
          visible:true,
          options:[]
        }
      ]
    });
  };

  const removeQuestion=(sectionId,questionId)=>{
    const section=sections.find((item)=>item.id===sectionId);
    if(!section)return;

    updateSection(sectionId,{
      questions:section.questions.filter(
        (question)=>question.id!==questionId
      )
    });
  };

  const moveSection=(index,direction)=>{
    const target=index+direction;
    if(target<0||target>=sections.length)return;

    const next=[...sections];
    [next[index],next[target]]=[
      next[target],
      next[index]
    ];
    setSections(next);
  };

  const moveQuestion=(sectionId,index,direction)=>{
    const section=sections.find((item)=>item.id===sectionId);
    const target=index+direction;

    if(
      !section||
      target<0||
      target>=section.questions.length
    )return;

    const questions=[...section.questions];
    [questions[index],questions[target]]=[
      questions[target],
      questions[index]
    ];
    updateSection(sectionId,{questions});
  };

  const dropQuestion=(sectionId,targetIndex)=>{
    if(!draggedQuestion||
      draggedQuestion.sectionId!==sectionId
    ){
      setDraggedQuestion(null);
      return;
    }

    const section=sections.find(
      (item)=>item.id===sectionId
    );

    if(!section){
      setDraggedQuestion(null);
      return;
    }

    const sourceIndex=section.questions.findIndex(
      (question)=>question.id===draggedQuestion.questionId
    );

    if(sourceIndex<0||sourceIndex===targetIndex){
      setDraggedQuestion(null);
      return;
    }

    const questions=[...section.questions];
    const[moved]=questions.splice(sourceIndex,1);
    questions.splice(targetIndex,0,moved);
    updateSection(sectionId,{questions});
    setDraggedQuestion(null);
  };

  const addSection=()=>{
    setSections((current)=>[
      ...current,
      {
        id:`section-${Date.now()}`,
        title:'New section',
        description:'Add guidance for this section.',
        enabled:true,
        questions:[]
      }
    ]);
  };

  const removeSection=(sectionId)=>{
    setSections((current)=>current.filter(
      (section)=>section.id!==sectionId
    ));
  };

  return <div className="modal-backdrop">
    <section className="modal-card questionnaire-template-editor">
      <header className="modal-heading">
        <div>
          <span className="eyebrow">Questionnaire template</span>
          <h2>Edit {template.name}</h2>
        </div>

        <button
          type="button"
          className="icon-btn"
          onClick={onClose}
          aria-label="Close editor"
        >
          <SafeIcon icon={FiX}/>
        </button>
      </header>

      <p className="template-editor-intro">
        Every client-facing section, question, answer choice, playlist link,
        music request, grand entrance row, and candle lighting row is editable.
      </p>

      <div className="template-editor-sections">
        {sections.map((section,index)=><section
          className="template-section-editor"
          key={section.id}
        >
          <div className="template-section-header">
            <div className="template-section-number">
              {String(index+1).padStart(2,'0')}
            </div>

            <div className="template-section-fields">
              <input
                value={section.title}
                onChange={(event)=>updateSection(
                  section.id,
                  {title:event.target.value}
                )}
                aria-label="Section title"
              />
              <input
                value={section.description||''}
                onChange={(event)=>updateSection(
                  section.id,
                  {description:event.target.value}
                )}
                aria-label="Section description"
              />
            </div>

            <div className="template-section-actions">
              <button
                type="button"
                className="icon-btn"
                disabled={index===0}
                onClick={()=>moveSection(index,-1)}
                aria-label="Move section up"
              >
                <SafeIcon icon={FiChevronUp}/>
              </button>

              <button
                type="button"
                className="icon-btn"
                disabled={index===sections.length-1}
                onClick={()=>moveSection(index,1)}
                aria-label="Move section down"
              >
                <SafeIcon icon={FiChevronDown}/>
              </button>

              <button
                type="button"
                className="icon-btn danger"
                onClick={()=>removeSection(section.id)}
                aria-label={`Delete ${section.title}`}
              >
                <SafeIcon icon={FiTrash2}/>
              </button>
            </div>
          </div>

          <div className="template-question-list">
            {section.questions.length===0
              ?<p className="question-options-empty">
                This client-facing section has no editable questions yet.
                Add a question to make it available to clients.
              </p>
              :section.questions.map((question,questionIndex)=><div
                className={`template-question-card ${
                  draggedQuestion?.questionId===question.id
                    ?'dragging'
                    :''
                }`}
                key={question.id}
                draggable
                onDragStart={()=>setDraggedQuestion({
                  sectionId:section.id,
                  questionId:question.id
                })}
                onDragOver={(event)=>event.preventDefault()}
                onDrop={()=>dropQuestion(
                  section.id,
                  questionIndex
                )}
                onDragEnd={()=>setDraggedQuestion(null)}
              >
                <div className="template-question-editor">
                  <span
                    className="template-question-handle"
                    title="Drag to reorder question"
                  >
                    <SafeIcon icon={FiGripVertical}/>
                  </span>

                  <input
                    value={question.title}
                    draggable={false}
                    onMouseDown={(event)=>event.stopPropagation()}
                    onDragStart={(event)=>event.preventDefault()}
                    onChange={(event)=>updateQuestion(
                      section.id,
                      question.id,
                      {title:event.target.value}
                    )}
                    aria-label="Question title"
                  />

                  <select
                    value={question.type}
                    onChange={(event)=>updateQuestion(
                      section.id,
                      question.id,
                      {type:event.target.value}
                    )}
                    aria-label="Question type"
                  >
                    {questionTypes.map((type)=><option key={type}>
                      {type}
                    </option>)}
                  </select>

                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(question.required)}
                      onChange={(event)=>updateQuestion(
                        section.id,
                        question.id,
                        {required:event.target.checked}
                      )}
                    />
                    Required
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={question.visible!==false}
                      onChange={(event)=>updateQuestion(
                        section.id,
                        question.id,
                        {visible:event.target.checked}
                      )}
                    />
                    Visible
                  </label>

                  <div className="template-question-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      disabled={questionIndex===0}
                      onClick={()=>moveQuestion(
                        section.id,
                        questionIndex,
                        -1
                      )}
                      aria-label="Move question up"
                    >
                      <SafeIcon icon={FiChevronUp}/>
                    </button>

                    <button
                      type="button"
                      className="icon-btn"
                      disabled={
                        questionIndex===section.questions.length-1
                      }
                      onClick={()=>moveQuestion(
                        section.id,
                        questionIndex,
                        1
                      )}
                      aria-label="Move question down"
                    >
                      <SafeIcon icon={FiChevronDown}/>
                    </button>

                    <button
                      type="button"
                      className="icon-btn danger"
                      onClick={()=>removeQuestion(
                        section.id,
                        question.id
                      )}
                      aria-label={`Delete ${question.title}`}
                    >
                      <SafeIcon icon={FiTrash2}/>
                    </button>
                  </div>
                </div>

                {question.type==='Repeating fields'
                  ?<RepeatingFieldsEditor
                    question={question}
                    onChange={(nextQuestion)=>updateQuestion(
                      section.id,
                      question.id,
                      nextQuestion
                    )}
                  />
                  :<QuestionOptionEditor
                    question={question}
                    onChange={(nextQuestion)=>updateQuestion(
                      section.id,
                      question.id,
                      nextQuestion
                    )}
                  />}
              </div>)}
          </div>

          <button
            type="button"
            className="button secondary template-add-question"
            onClick={()=>addQuestion(section.id)}
          >
            <SafeIcon icon={FiPlus}/>
            Add question
          </button>
        </section>)}
      </div>

      <button
        type="button"
        className="button secondary template-add-section"
        onClick={addSection}
      >
        <SafeIcon icon={FiPlus}/>
        Add section
      </button>

      <footer className="modal-actions">
        <button
          type="button"
          className="button secondary"
          onClick={onClose}
        >
          Cancel
        </button>

        <button
          type="button"
          className="button primary"
          onClick={()=>onSave(sections)}
        >
          <SafeIcon icon={FiSave}/>
          Save template
        </button>
      </footer>
    </section>
  </div>;
}

export default QuestionnaireTemplateEditor;